import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux'
import {useTranslation} from 'react-i18next';
import {StyledInvoiceDetailsPanel} from './StyledInvoiceDetailsPanel';
import InvoiceDetails from '../InvoiceDetails';
import InvoiceSelector from '../InvoiceSelector';
import SpinSmall from '../../../SpinSmall';
import {expenseActions} from '../../../../state/actions/expense.actions';
import {expenseTypesConstants} from '../../../../constants';
import {expensesHelpers, fileHelpers, transactionsHelpers} from '../../../../utils/helpers';
import {alertActions} from '../../../../state/actions/alert.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';

const defaultAttachment = {
  contentType: undefined,
  data: undefined,
  file: undefined,
  name: undefined
}

const InvoiceDetailsPanel = ({
  dispatch,
  batchCreateInvoices,
  getExpenseAttachment,
  getExpenseRelatedInvoices,
  linkExpense,
  updateExpense,


  edit = true,
  expense,
  onExpenseUpdate,
  onInvoiceRemove,
  ...rest
}) => {
  const [attachment, setAttachment] = useState(defaultAttachment);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
  const [relatedInvoices, setRelatedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canStartEditingInvoice, setCanStartEditingInvoice] = useState(false);

  const [t] = useTranslation('main');

  const expenseId = useMemo(() => expense?.id, [expense?.id]);

  const showInvoiceAttachmentWarning = () => {
    alertActions.warning(t('validation.invoiceCannotBeAttached'), 4000, 350);
  }

  const finishAttachmentLoading = () => setIsAttachmentLoading(false);

  const finishLoading = () => setLoading(false);

  const startLoading = () => setLoading(true);

  const showMainSpinner = useMemo(() => isAttachmentLoading || loading, [isAttachmentLoading, loading]);

  const invoiceType = useMemo(() => transactionsHelpers.getTransactionInvoiceType({expense}), [expense]);

  useEffect(() => {
    const enableEdit = !expensesHelpers.isDefinedExpenseType(expense);

    if (canStartEditingInvoice !== enableEdit) {
      setCanStartEditingInvoice(enableEdit);
    }
  }, [expense]); // eslint-disable-line react-hooks/exhaustive-deps

  const getAttachmentDetails = (expenseId) => {
    if (!expenseId) return;

    setIsAttachmentLoading(true);
    getExpenseAttachment({
      id: expenseId,
      successCallback: (data) => {
        const contentType = data['content-type']
        const file = `data:${contentType};base64,${data.file}`;
        setAttachment({
          data,
          contentType,
          file,
          name: data.filename
        });
        finishAttachmentLoading();
      },
      errorCallback: () => {
        setAttachment(defaultAttachment);
        finishAttachmentLoading();
      }
    });
  }

  const getRelatedInvoices = (expenseId) => {
    if (!expenseId) return;

    const callbackFunc = (data) => {
      setRelatedInvoices(data);
      setLoading(false);
    }

    setLoading(true);
    getExpenseRelatedInvoices({
      id: expenseId,
      successCallback: callbackFunc,
      errorCallback: () => callbackFunc([])
    });
  }

  useEffect(() => {
    if (expense) {
      const expenseId = expense?.id;
      const isDefinedExpenseType = expensesHelpers.isDefinedExpenseType({expense});

      if (expenseId && expense.attachment_url) {
        getAttachmentDetails(expenseId);
      } else if (attachment.file) {
        setAttachment(defaultAttachment)
      }

      if (!isDefinedExpenseType) {
        getRelatedInvoices(expenseId);
      }
    }
  }, [expense?.attachment_url, expense?.expense_type, expense?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitForm = (data, successCallback, errorCallback) => {
    const { expense_type } = data;
    if (!expense) return; // Early exit if no expense.

    const isExpenseTypeUpdated = expense_type !== undefined && expense_type !== expense.expense_type;

    updateExpense({
      id: expense.id,
      data: isExpenseTypeUpdated ? { expense_type } : data,
      ...(isExpenseTypeUpdated && { propertyName: 'expense_type' }),
      successCallback: (data) => {
        onExpenseUpdate?.(data);
        successCallback?.();
      },
      errorCallback
    });
  };

  const handleRemoveInvoice = () => {
    if (onInvoiceRemove) {
      setIsAttachmentLoading(true);
      onInvoiceRemove(
        () => {
          setAttachment(defaultAttachment);
          finishAttachmentLoading();
        },
        finishAttachmentLoading
      );
    }
  }

  const handleEditInvoice = () => setCanStartEditingInvoice(true);

  const handleSetNoReceipt = (e, callbackFunc) => {
    const data = {expense_type: expenseTypesConstants.NO_RECEIPT};
    handleSubmitForm(
      data,
      () => {
        onExpenseUpdate({
          ...expense,
          ...data,
          expense_invoice_status: undefined,
        });
        callbackFunc?.();
      },
      callbackFunc
    );
  }

  const handleLinkExpense = (invoiceId) => {
    if (!expenseId) {
      finishLoading();
      showInvoiceAttachmentWarning();
      return;
    }

    const data = {expense_type: expenseTypesConstants[invoiceType]};

    startLoading();

    linkExpense({
      id: invoiceId,
      data: {expense_id: expenseId},
      successCallback: () => {
        updateExpense({
          id: expenseId,
          data,
          propertyName: 'expense_type',
          successCallback: (response) => {
            onExpenseUpdate?.(response);
            finishLoading();
          },
          errorCallback: finishLoading,
        });
      },
      errorCallback: finishLoading
    });
  }

  const handleInvoiceSelection = (e, invoiceId) => handleLinkExpense(invoiceId);

  const handleAddInvoice = async (files) => {
    if (!files?.length) return;

    if (!expenseId) {
      showInvoiceAttachmentWarning();
      return;
    }

    startLoading();

    const file = files[0];
    const fileBase64 = await fileHelpers.getBase64(file.originFileObj);

    batchCreateInvoices(
      {invoices: [{base64_file: fileBase64, file_name: file.name}]},
      (invoices) => {
        if (invoices.length > 0) {
          handleLinkExpense(invoices[0].id);
        }
      },
      finishLoading
    )
  }

  return (
    <StyledInvoiceDetailsPanel {...rest}>

      <SpinSmall spinning={showMainSpinner}>
        {canStartEditingInvoice ? (
          <InvoiceSelector
            onAddInvoice={handleAddInvoice}
            onInvoiceSelect={handleInvoiceSelection}
            onSelectNoReceipt={handleSetNoReceipt}
            invoices={relatedInvoices}
          />
        ) : (
          <InvoiceDetails
            attachment={attachment}
            expense={expense}
            isCloseDisable={!edit}
            isEditDisable={!edit}
            loadingAttachment={isAttachmentLoading}
            onClose={handleRemoveInvoice}
            onEdit={handleEditInvoice}
            onSubmit={handleSubmitForm}
          />
        )}
      </SpinSmall>
    </StyledInvoiceDetailsPanel>
  );
}

InvoiceDetailsPanel.propTypes = {
  edit: PropTypes.bool,
  expense: PropTypes.object,
  onExpenseUpdate: PropTypes.func,
  onInvoiceRemove: PropTypes.func,
}

const mapDispatchToProps = {
  batchCreateInvoices: invoicesActions.batchCreateInvoices,
  getExpenseAttachment: expenseActions.getExpenseAttachment,
  getExpenseRelatedInvoices: expenseActions.getExpenseRelatedInvoices,
  linkExpense: invoicesActions.linkExpense,
  updateExpense: expenseActions.updateExpense,
}

export default connect(null, mapDispatchToProps)(InvoiceDetailsPanel);
